import { ApiResult, HttpClient } from "../http.js";
import {
  AggregatedBudgetChildProjectInput,
  AggregatedBudgetStatusDto,
  ProjectBudgetAllocationDto,
  ProjectBudgetAlertThresholdDto,
  ProjectBudgetDto,
  ProjectBudgetStatusDto,
} from "../types/project-budgets.js";

export interface CreateProjectBudgetPayload {
  projectId: string;
  totalBudget: number;
  budgetedHours: number;
  contingencyPercent: number;
  currency?: string | null;
  createdById?: string | null;
  createdByName?: string | null;
  allocations?: ProjectBudgetAllocationDto[] | null;
  alertThresholds?: ProjectBudgetAlertThresholdDto[] | null;
  expectedStartDate?: string | null;
  expectedEndDate?: string | null;
  isEnabled?: boolean;
  id?: string;
}

export interface UpdateProjectBudgetPayload {
  totalBudget: number;
  budgetedHours: number;
  contingencyPercent: number;
  currency?: string | null;
  allocations?: ProjectBudgetAllocationDto[] | null;
  alertThresholds?: ProjectBudgetAlertThresholdDto[] | null;
  expectedStartDate?: string | null;
  expectedEndDate?: string | null;
  isEnabled: boolean;
}

export class ProjectBudgetsApi {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ApiResult<ProjectBudgetDto[]>> {
    return this.http.get<ProjectBudgetDto[]>("/api/project-budgets");
  }

  create(payload: CreateProjectBudgetPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/project-budgets", { body: payload });
  }

  get(id: string): Promise<ApiResult<ProjectBudgetDto>> {
    return this.http.get<ProjectBudgetDto>(`/api/project-budgets/${encodeURIComponent(id)}`);
  }

  getByProject(projectId: string): Promise<ApiResult<ProjectBudgetDto | undefined>> {
    return this.http.get<ProjectBudgetDto | undefined>(`/api/projects/${encodeURIComponent(projectId)}/budget`);
  }

  getStatus(projectId: string): Promise<ApiResult<ProjectBudgetStatusDto | undefined>> {
    return this.http.get<ProjectBudgetStatusDto | undefined>(`/api/projects/${encodeURIComponent(projectId)}/budget/status`);
  }

  /**
   * Aggregated budget status across the parent project and the supplied children.
   * Returns `null` when the parent has no enabled budget. The caller passes the child
   * set it already holds so the included projects match the rest of the UI.
   */
  getAggregatedStatus(
    projectId: string,
    children: AggregatedBudgetChildProjectInput[],
  ): Promise<ApiResult<AggregatedBudgetStatusDto | null>> {
    return this.http.post<AggregatedBudgetStatusDto | null>(
      `/api/projects/${encodeURIComponent(projectId)}/budget/status/aggregated`,
      { body: { children } },
    );
  }

  update(id: string, payload: UpdateProjectBudgetPayload): Promise<ApiResult<ProjectBudgetDto>> {
    return this.http.put<ProjectBudgetDto>(`/api/project-budgets/${encodeURIComponent(id)}`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/project-budgets/${encodeURIComponent(id)}`);
  }
}
